import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Tag,
  Barcode,
  Truck,
  Settings,
  Save,
  RotateCcw,
  Printer,
  Eye,
  Check,
  X,
} from "lucide-react";
import JsBarcode from "jsbarcode";

const API = `${window.location.origin}/api`;

// مكون معاينة ملصق السعر
const PriceLabelPreview = ({ settings, product }) => {
  const style = {
    width: `${settings.width}mm`,
    height: `${settings.height}mm`,
    border: "1px solid #333",
    padding: "2mm",
    fontFamily: "Arial, sans-serif",
    fontSize: `${settings.font_size}px`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "white",
    color: "black",
  };

  return (
    <div style={style} className="label-preview">
      {settings.show_shop_name && (
        <div style={{ fontSize: "8px", marginBottom: "2px" }}>اسم المحل</div>
      )}
      {settings.show_name && (
        <div style={{ fontWeight: "bold", textAlign: "center", marginBottom: "2px" }}>
          {product?.name || "اسم المنتج"}
        </div>
      )}
      {settings.show_code && (
        <div style={{ fontSize: "9px", color: "#666" }}>
          {product?.code || "P-001"}
        </div>
      )}
      {settings.show_barcode && (
        <svg id="price-barcode-preview" style={{ margin: "2px 0" }}></svg>
      )}
      {settings.show_price && (
        <div style={{ fontWeight: "bold", fontSize: `${settings.font_size + 4}px` }}>
          {product?.sale_price?.toLocaleString() || "15,000"}
          {settings.show_currency && " د.ع"}
        </div>
      )}
    </div>
  );
};

// مكون معاينة ملصق الباركود
const BarcodeLabelPreview = ({ settings, product }) => {
  const style = {
    width: `${settings.width}mm`,
    height: `${settings.height}mm`,
    border: "1px solid #333",
    padding: "1mm",
    fontFamily: "Arial, sans-serif",
    fontSize: `${settings.font_size}px`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "white",
    color: "black",
  };

  return (
    <div style={style} className="label-preview">
      {settings.show_name && (
        <div style={{ fontSize: "9px", textAlign: "center", marginBottom: "1px" }}>
          {product?.name || "اسم المنتج"}
        </div>
      )}
      {settings.show_barcode && (
        <svg id="barcode-label-preview"></svg>
      )}
      {settings.show_barcode_number && (
        <div style={{ fontSize: "8px", fontFamily: "monospace" }}>
          {product?.barcode || "6281234567890"}
        </div>
      )}
      {settings.show_code && (
        <div style={{ fontSize: "8px", color: "#666" }}>
          {product?.code || "P-001"}
        </div>
      )}
    </div>
  );
};

// مكون معاينة ملصق الشحن
const ShippingLabelPreview = ({ settings }) => {
  const style = {
    width: `${settings.width}mm`,
    height: `${Math.min(settings.height, 100)}mm`,
    border: "2px solid #333",
    padding: "3mm",
    fontFamily: "Arial, sans-serif",
    fontSize: `${settings.font_size}px`,
    backgroundColor: "white",
    color: "black",
    overflow: "hidden",
  };

  return (
    <div style={style} className="label-preview">
      {(settings.show_shop_name || settings.show_shop_phone) && (
        <div style={{ borderBottom: "1px solid #ccc", paddingBottom: "2mm", marginBottom: "2mm" }}>
          {settings.show_shop_name && <div style={{ fontWeight: "bold" }}>اسم المحل</div>}
          {settings.show_shop_phone && <div style={{ fontSize: "10px" }}>📞 07XX XXX XXXX</div>}
          {settings.show_shop_address && <div style={{ fontSize: "9px" }}>العنوان</div>}
        </div>
      )}
      
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: "bold", marginBottom: "1mm" }}>إلى:</div>
        {settings.show_customer_name && <div>اسم العميل</div>}
        {settings.show_customer_phone && <div style={{ fontSize: "11px" }}>📞 07XX XXX XXXX</div>}
        {settings.show_customer_address && <div style={{ fontSize: "10px" }}>عنوان العميل</div>}
      </div>
      
      <div style={{ borderTop: "1px dashed #ccc", paddingTop: "2mm", marginTop: "2mm", fontSize: "10px" }}>
        {settings.show_order_number && <span style={{ marginLeft: "10px" }}>طلب: #12345</span>}
        {settings.show_date && <span>التاريخ: 2026/03/17</span>}
        {settings.show_items_count && <div>عدد القطع: 3</div>}
      </div>
    </div>
  );
};

export default function LabelsSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [sizes, setSizes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("price");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [printQuantity, setPrintQuantity] = useState(1);

  useEffect(() => {
    fetchSettings();
    fetchSizes();
    fetchProducts();
  }, []);

  useEffect(() => {
    // تحديث الباركود في المعاينة
    if (settings?.price_label?.show_barcode) {
      try {
        JsBarcode("#price-barcode-preview", selectedProduct?.barcode || "6281234567890", {
          format: "CODE128",
          width: 1.5,
          height: settings.price_label.barcode_height || 30,
          displayValue: false,
          margin: 0,
        });
      } catch (e) {}
    }
    if (settings?.barcode_label?.show_barcode) {
      try {
        JsBarcode("#barcode-label-preview", selectedProduct?.barcode || "6281234567890", {
          format: settings.barcode_label.barcode_type || "CODE128",
          width: 1.5,
          height: settings.barcode_label.barcode_height || 40,
          displayValue: false,
          margin: 0,
        });
      } catch (e) {}
    }
  }, [settings, selectedProduct]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/labels/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("فشل في تحميل الإعدادات");
    } finally {
      setLoading(false);
    }
  };

  const fetchSizes = async () => {
    try {
      const response = await axios.get(`${API}/labels/sizes`);
      setSizes(response.data);
    } catch (error) {
      console.error("Error fetching sizes:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
      if (response.data.length > 0) {
        setSelectedProduct(response.data[0]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/labels/settings`, settings);
      toast.success("تم حفظ الإعدادات بنجاح");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("فشل في حفظ الإعدادات");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("هل أنت متأكد من إعادة تعيين جميع الإعدادات للقيم الافتراضية؟")) {
      return;
    }
    try {
      const response = await axios.post(`${API}/labels/reset`);
      setSettings(response.data);
      toast.success("تم إعادة تعيين الإعدادات");
    } catch (error) {
      console.error("Error resetting settings:", error);
      toast.error("فشل في إعادة تعيين الإعدادات");
    }
  };

  const updatePriceSettings = (key, value) => {
    setSettings({
      ...settings,
      price_label: { ...settings.price_label, [key]: value },
    });
  };

  const updateBarcodeSettings = (key, value) => {
    setSettings({
      ...settings,
      barcode_label: { ...settings.barcode_label, [key]: value },
    });
  };

  const updateShippingSettings = (key, value) => {
    setSettings({
      ...settings,
      shipping_label: { ...settings.shipping_label, [key]: value },
    });
  };

  const printLabels = () => {
    const labelType = activeTab;
    const labelSettings = settings[`${labelType}_label`];
    
    const printWindow = window.open("", "_blank");
    let labelsHTML = "";
    
    for (let i = 0; i < printQuantity; i++) {
      if (labelType === "price") {
        labelsHTML += generatePriceLabelHTML(labelSettings, selectedProduct);
      } else if (labelType === "barcode") {
        labelsHTML += generateBarcodeLabelHTML(labelSettings, selectedProduct);
      } else {
        labelsHTML += generateShippingLabelHTML(labelSettings);
      }
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>طباعة الملصقات</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <style>
          @page { size: A4; margin: 5mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; }
          .labels-container { 
            display: flex; 
            flex-wrap: wrap; 
            gap: 2mm;
            justify-content: flex-start;
          }
          .label {
            page-break-inside: avoid;
            border: 1px solid #ccc;
            padding: 2mm;
          }
        </style>
      </head>
      <body>
        <div class="labels-container">
          ${labelsHTML}
        </div>
        <script>
          // تفعيل الباركود
          document.querySelectorAll('.barcode-svg').forEach((svg, index) => {
            const barcode = svg.dataset.barcode || '0000000000000';
            const height = parseInt(svg.dataset.height) || 30;
            const format = svg.dataset.format || 'CODE128';
            try {
              JsBarcode(svg, barcode, {
                format: format,
                width: 1.5,
                height: height,
                displayValue: false,
                margin: 0
              });
            } catch(e) { console.log(e); }
          });
          setTimeout(() => { window.print(); }, 500);
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    setIsPrintDialogOpen(false);
  };

  const generatePriceLabelHTML = (settings, product) => {
    return `
      <div class="label" style="width:${settings.width}mm;height:${settings.height}mm;text-align:center;display:flex;flex-direction:column;justify-content:center;align-items:center;">
        ${settings.show_shop_name ? `<div style="font-size:8px;">اسم المحل</div>` : ""}
        ${settings.show_name ? `<div style="font-weight:bold;font-size:${settings.font_size}px;">${product?.name || "اسم المنتج"}</div>` : ""}
        ${settings.show_code ? `<div style="font-size:9px;color:#666;">${product?.code || "P-001"}</div>` : ""}
        ${settings.show_barcode ? `<svg class="barcode-svg" data-barcode="${product?.barcode || "6281234567890"}" data-height="${settings.barcode_height}"></svg>` : ""}
        ${settings.show_price ? `<div style="font-weight:bold;font-size:${settings.font_size + 4}px;">${(product?.sale_price || 15000).toLocaleString()}${settings.show_currency ? " د.ع" : ""}</div>` : ""}
      </div>
    `;
  };

  const generateBarcodeLabelHTML = (settings, product) => {
    return `
      <div class="label" style="width:${settings.width}mm;height:${settings.height}mm;text-align:center;display:flex;flex-direction:column;justify-content:center;align-items:center;">
        ${settings.show_name ? `<div style="font-size:9px;">${product?.name || "اسم المنتج"}</div>` : ""}
        ${settings.show_barcode ? `<svg class="barcode-svg" data-barcode="${product?.barcode || "6281234567890"}" data-height="${settings.barcode_height}" data-format="${settings.barcode_type}"></svg>` : ""}
        ${settings.show_barcode_number ? `<div style="font-size:8px;font-family:monospace;">${product?.barcode || "6281234567890"}</div>` : ""}
        ${settings.show_code ? `<div style="font-size:8px;color:#666;">${product?.code || "P-001"}</div>` : ""}
      </div>
    `;
  };

  const generateShippingLabelHTML = (settings) => {
    return `
      <div class="label" style="width:${settings.width}mm;height:${settings.height}mm;padding:3mm;">
        <div style="border-bottom:1px solid #ccc;padding-bottom:2mm;margin-bottom:2mm;">
          ${settings.show_shop_name ? `<div style="font-weight:bold;">اسم المحل</div>` : ""}
          ${settings.show_shop_phone ? `<div style="font-size:10px;">📞 07XX XXX XXXX</div>` : ""}
          ${settings.show_shop_address ? `<div style="font-size:9px;">عنوان المحل</div>` : ""}
        </div>
        <div style="margin-bottom:3mm;">
          <div style="font-weight:bold;margin-bottom:1mm;">إلى:</div>
          ${settings.show_customer_name ? `<div>اسم العميل</div>` : ""}
          ${settings.show_customer_phone ? `<div style="font-size:11px;">📞 07XX XXX XXXX</div>` : ""}
          ${settings.show_customer_address ? `<div style="font-size:10px;">عنوان العميل الكامل</div>` : ""}
        </div>
        <div style="border-top:1px dashed #ccc;padding-top:2mm;font-size:10px;">
          ${settings.show_order_number ? `<span style="margin-left:10px;">طلب: #12345</span>` : ""}
          ${settings.show_date ? `<span>التاريخ: ${new Date().toLocaleDateString('ar-IQ')}</span>` : ""}
          ${settings.show_items_count ? `<div>عدد القطع: 3</div>` : ""}
          ${settings.show_notes ? `<div style="margin-top:2mm;font-style:italic;">ملاحظات...</div>` : ""}
        </div>
      </div>
    `;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="labels-settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Tag className="w-7 h-7 text-amber-500" />
            إعدادات الملصقات
          </h1>
          <p className="text-slate-400 text-sm mt-1">تخصيص ملصقات الأسعار والباركود والشحن</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleReset}
            variant="outline"
            className="border-slate-600 text-slate-300"
          >
            <RotateCcw className="w-4 h-4 me-2" />
            إعادة تعيين
          </Button>
          <Button
            onClick={handleSave}
            className="bg-amber-600 hover:bg-amber-700"
            disabled={saving}
            data-testid="save-settings-btn"
          >
            <Save className="w-4 h-4 me-2" />
            {saving ? "جاري الحفظ..." : "حفظ الإعدادات"}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="price" className="data-[state=active]:bg-amber-600">
            <Tag className="w-4 h-4 me-2" />
            ملصقات الأسعار
          </TabsTrigger>
          <TabsTrigger value="barcode" className="data-[state=active]:bg-amber-600">
            <Barcode className="w-4 h-4 me-2" />
            ملصقات الباركود
          </TabsTrigger>
          <TabsTrigger value="shipping" className="data-[state=active]:bg-amber-600">
            <Truck className="w-4 h-4 me-2" />
            ملصقات الشحن
          </TabsTrigger>
        </TabsList>

        {/* Price Labels Tab */}
        <TabsContent value="price" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Settings */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-amber-500" />
                  إعدادات ملصق السعر
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Size */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">العرض (mm)</Label>
                    <Input
                      type="number"
                      value={settings?.price_label?.width || 50}
                      onChange={(e) => updatePriceSettings("width", parseFloat(e.target.value))}
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">الارتفاع (mm)</Label>
                    <Input
                      type="number"
                      value={settings?.price_label?.height || 30}
                      onChange={(e) => updatePriceSettings("height", parseFloat(e.target.value))}
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                </div>

                {/* Quick Size Select */}
                <div className="space-y-2">
                  <Label className="text-slate-300">حجم سريع</Label>
                  <div className="flex gap-2">
                    {sizes.price?.map((size) => (
                      <Button
                        key={size.name}
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          updatePriceSettings("width", size.width);
                          updatePriceSettings("height", size.height);
                        }}
                        className="border-slate-600 text-slate-300 hover:bg-amber-600/20"
                      >
                        {size.name}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Font Size */}
                <div className="space-y-2">
                  <Label className="text-slate-300">حجم الخط</Label>
                  <Input
                    type="number"
                    value={settings?.price_label?.font_size || 12}
                    onChange={(e) => updatePriceSettings("font_size", parseInt(e.target.value))}
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>

                {/* Toggles */}
                <div className="space-y-3">
                  <Label className="text-slate-300">عناصر الملصق</Label>
                  {[
                    { key: "show_name", label: "اسم المنتج" },
                    { key: "show_code", label: "كود المنتج" },
                    { key: "show_barcode", label: "الباركود" },
                    { key: "show_price", label: "السعر" },
                    { key: "show_currency", label: "العملة" },
                    { key: "show_shop_name", label: "اسم المحل" },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between p-2 bg-slate-900/50 rounded">
                      <span className="text-slate-300">{label}</span>
                      <button
                        onClick={() => updatePriceSettings(key, !settings?.price_label?.[key])}
                        className={`p-1.5 rounded transition-colors ${
                          settings?.price_label?.[key]
                            ? "bg-green-500/20 text-green-400"
                            : "bg-slate-700 text-slate-500"
                        }`}
                      >
                        {settings?.price_label?.[key] ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                </div>

                {/* Print Button */}
                <Button
                  onClick={() => setIsPrintDialogOpen(true)}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <Printer className="w-4 h-4 me-2" />
                  طباعة ملصقات
                </Button>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-amber-500" />
                  معاينة
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Product Selection */}
                <div className="mb-4">
                  <Label className="text-slate-300 mb-2 block">اختر منتج للمعاينة</Label>
                  <Select
                    value={selectedProduct?.id || ""}
                    onValueChange={(value) => {
                      const product = products.find((p) => p.id === value);
                      setSelectedProduct(product);
                    }}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue placeholder="اختر منتج" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id} className="text-white">
                          {product.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preview Box */}
                <div className="flex justify-center items-center p-6 bg-slate-900 rounded-lg min-h-[200px]">
                  {settings?.price_label && (
                    <PriceLabelPreview settings={settings.price_label} product={selectedProduct} />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Barcode Labels Tab */}
        <TabsContent value="barcode" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Settings */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-amber-500" />
                  إعدادات ملصق الباركود
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Size */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">العرض (mm)</Label>
                    <Input
                      type="number"
                      value={settings?.barcode_label?.width || 60}
                      onChange={(e) => updateBarcodeSettings("width", parseFloat(e.target.value))}
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">الارتفاع (mm)</Label>
                    <Input
                      type="number"
                      value={settings?.barcode_label?.height || 25}
                      onChange={(e) => updateBarcodeSettings("height", parseFloat(e.target.value))}
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                </div>

                {/* Barcode Type */}
                <div className="space-y-2">
                  <Label className="text-slate-300">نوع الباركود</Label>
                  <Select
                    value={settings?.barcode_label?.barcode_type || "CODE128"}
                    onValueChange={(value) => updateBarcodeSettings("barcode_type", value)}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="CODE128" className="text-white">CODE128</SelectItem>
                      <SelectItem value="EAN13" className="text-white">EAN-13</SelectItem>
                      <SelectItem value="EAN8" className="text-white">EAN-8</SelectItem>
                      <SelectItem value="UPC" className="text-white">UPC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Barcode Height */}
                <div className="space-y-2">
                  <Label className="text-slate-300">ارتفاع الباركود (px)</Label>
                  <Input
                    type="number"
                    value={settings?.barcode_label?.barcode_height || 40}
                    onChange={(e) => updateBarcodeSettings("barcode_height", parseInt(e.target.value))}
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>

                {/* Toggles */}
                <div className="space-y-3">
                  <Label className="text-slate-300">عناصر الملصق</Label>
                  {[
                    { key: "show_name", label: "اسم المنتج" },
                    { key: "show_code", label: "كود المنتج" },
                    { key: "show_barcode", label: "الباركود" },
                    { key: "show_barcode_number", label: "رقم الباركود" },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between p-2 bg-slate-900/50 rounded">
                      <span className="text-slate-300">{label}</span>
                      <button
                        onClick={() => updateBarcodeSettings(key, !settings?.barcode_label?.[key])}
                        className={`p-1.5 rounded transition-colors ${
                          settings?.barcode_label?.[key]
                            ? "bg-green-500/20 text-green-400"
                            : "bg-slate-700 text-slate-500"
                        }`}
                      >
                        {settings?.barcode_label?.[key] ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                </div>

                {/* Print Button */}
                <Button
                  onClick={() => setIsPrintDialogOpen(true)}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <Printer className="w-4 h-4 me-2" />
                  طباعة ملصقات
                </Button>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-amber-500" />
                  معاينة
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-center items-center p-6 bg-slate-900 rounded-lg min-h-[200px]">
                  {settings?.barcode_label && (
                    <BarcodeLabelPreview settings={settings.barcode_label} product={selectedProduct} />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Shipping Labels Tab */}
        <TabsContent value="shipping" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Settings */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-amber-500" />
                  إعدادات ملصق الشحن
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Size */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">العرض (mm)</Label>
                    <Input
                      type="number"
                      value={settings?.shipping_label?.width || 100}
                      onChange={(e) => updateShippingSettings("width", parseFloat(e.target.value))}
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">الارتفاع (mm)</Label>
                    <Input
                      type="number"
                      value={settings?.shipping_label?.height || 150}
                      onChange={(e) => updateShippingSettings("height", parseFloat(e.target.value))}
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                </div>

                {/* Quick Size */}
                <div className="space-y-2">
                  <Label className="text-slate-300">حجم سريع</Label>
                  <div className="flex gap-2">
                    {sizes.shipping?.map((size) => (
                      <Button
                        key={size.name}
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          updateShippingSettings("width", size.width);
                          updateShippingSettings("height", size.height);
                        }}
                        className="border-slate-600 text-slate-300 hover:bg-amber-600/20"
                      >
                        {size.name}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Shop Info Toggles */}
                <div className="space-y-3">
                  <Label className="text-slate-300">معلومات المحل</Label>
                  {[
                    { key: "show_shop_name", label: "اسم المحل" },
                    { key: "show_shop_phone", label: "هاتف المحل" },
                    { key: "show_shop_address", label: "عنوان المحل" },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between p-2 bg-slate-900/50 rounded">
                      <span className="text-slate-300">{label}</span>
                      <button
                        onClick={() => updateShippingSettings(key, !settings?.shipping_label?.[key])}
                        className={`p-1.5 rounded transition-colors ${
                          settings?.shipping_label?.[key]
                            ? "bg-green-500/20 text-green-400"
                            : "bg-slate-700 text-slate-500"
                        }`}
                      >
                        {settings?.shipping_label?.[key] ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                </div>

                {/* Customer Info Toggles */}
                <div className="space-y-3">
                  <Label className="text-slate-300">معلومات العميل</Label>
                  {[
                    { key: "show_customer_name", label: "اسم العميل" },
                    { key: "show_customer_phone", label: "هاتف العميل" },
                    { key: "show_customer_address", label: "عنوان العميل" },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between p-2 bg-slate-900/50 rounded">
                      <span className="text-slate-300">{label}</span>
                      <button
                        onClick={() => updateShippingSettings(key, !settings?.shipping_label?.[key])}
                        className={`p-1.5 rounded transition-colors ${
                          settings?.shipping_label?.[key]
                            ? "bg-green-500/20 text-green-400"
                            : "bg-slate-700 text-slate-500"
                        }`}
                      >
                        {settings?.shipping_label?.[key] ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                </div>

                {/* Order Info Toggles */}
                <div className="space-y-3">
                  <Label className="text-slate-300">معلومات الطلب</Label>
                  {[
                    { key: "show_order_number", label: "رقم الطلب" },
                    { key: "show_date", label: "التاريخ" },
                    { key: "show_items_count", label: "عدد القطع" },
                    { key: "show_notes", label: "الملاحظات" },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between p-2 bg-slate-900/50 rounded">
                      <span className="text-slate-300">{label}</span>
                      <button
                        onClick={() => updateShippingSettings(key, !settings?.shipping_label?.[key])}
                        className={`p-1.5 rounded transition-colors ${
                          settings?.shipping_label?.[key]
                            ? "bg-green-500/20 text-green-400"
                            : "bg-slate-700 text-slate-500"
                        }`}
                      >
                        {settings?.shipping_label?.[key] ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                </div>

                {/* Print Button */}
                <Button
                  onClick={() => setIsPrintDialogOpen(true)}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <Printer className="w-4 h-4 me-2" />
                  طباعة ملصقات
                </Button>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-amber-500" />
                  معاينة
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-center items-center p-6 bg-slate-900 rounded-lg min-h-[300px]">
                  {settings?.shipping_label && (
                    <ShippingLabelPreview settings={settings.shipping_label} />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Print Dialog */}
      <Dialog open={isPrintDialogOpen} onOpenChange={setIsPrintDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-green-500" />
              طباعة الملصقات
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">عدد الملصقات</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={printQuantity}
                onChange={(e) => setPrintQuantity(parseInt(e.target.value) || 1)}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            {activeTab !== "shipping" && (
              <div className="space-y-2">
                <Label className="text-slate-300">المنتج</Label>
                <Select
                  value={selectedProduct?.id || ""}
                  onValueChange={(value) => {
                    const product = products.find((p) => p.id === value);
                    setSelectedProduct(product);
                  }}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue placeholder="اختر منتج" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id} className="text-white">
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsPrintDialogOpen(false)}
              className="border-slate-600 text-slate-300"
            >
              إلغاء
            </Button>
            <Button
              onClick={printLabels}
              className="bg-green-600 hover:bg-green-700"
            >
              <Printer className="w-4 h-4 me-2" />
              طباعة
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
